import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchPermissionAdmins, fetchPermissionCatalog } from "../../api/access";
import { getAccessErrorMessage } from "../../utils/accessErrors";
import { splitCatalog } from "../../utils/permissionScreens";

/**
 * 권한 카탈로그(도메인별 그룹) 조회.
 * catalog 에는 화면에 보일 키만 남긴다(hidden 키·키가 모두 숨김인 도메인 제외). hiddenKeys 는 역할 저장 등에서 걸러낼 숨김 키,
 * permissionByKey 는 보이는 키의 카탈로그 항목(screens 포함)이다.
 */
export function useCatalog() {
  const [rawCatalog, setRawCatalog] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchPermissionCatalog()
      .then((data) => !cancelled && setRawCatalog(data))
      .catch((err) => !cancelled && setError(getAccessErrorMessage(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const { catalog, hiddenKeys, permissionByKey } = useMemo(() => splitCatalog(rawCatalog), [rawCatalog]);

  return { catalog, hiddenKeys, permissionByKey, error, loading };
}

// 어드민 목록 전용 API가 없어 기능별 보기 API(전체 어드민 반환)로 목록을 얻는다. access.permission.read 는 항상 카탈로그에 있다.
const ADMIN_LIST_SOURCE_KEY = "access.permission.read";

/** 전체 어드민 목록 [{ adminUserId, name, email }] */
export function useAdminList() {
  const [admins, setAdmins] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await fetchPermissionAdmins(ADMIN_LIST_SOURCE_KEY);
      setAdmins(
        (data?.admins ?? []).map(({ adminUserId, name, email }) => ({ adminUserId, name, email })),
      );
    } catch (err) {
      setError(getAccessErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { admins, error };
}

export const adminLabel = (admin) =>
  admin ? `${admin.name || "(이름 없음)"} · ${admin.email || "-"} (#${admin.adminUserId})` : "-";

/** datetime-local 값 → ISO 8601 (브라우저 로컬 시간대 기준) */
export function localInputToIso(value) {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}
